import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
  EditOutlined as EditOutlinedIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { surface } from '../../theme';
import ExpenseService from '../../services/ExpenseService';
import RangePicker from '../../components/analytics/RangePicker';
import { ChannelAvatar, INK, Section, StatTile } from '../../components/analytics/AnalyticsParts';
import { recentMonths, selectionQuery } from '../../utils/analyticsRange';
import { formatDate, formatMoney, formatNumber } from '../../utils/analyticsFormat';
import { DeleteExpenseDialog, ExpenseDialog } from './ExpenseDialogs';

const RANGE_KEY = 'expenses.range';
const GENERAL = 'general';

// The current month until the user picks another range.
function loadSelection() {
  try {
    const saved = JSON.parse(localStorage.getItem(RANGE_KEY));
    if (saved?.type) return saved;
  } catch (_) {
    // Storage can be unavailable; fall back to the current month.
  }
  return { type: 'month', ...recentMonths(1)[0] };
}

function saveSelection(selection) {
  try {
    localStorage.setItem(RANGE_KEY, JSON.stringify(selection));
  } catch (_) {
    // Not remembering the range is fine.
  }
}

function rangeLabel(range) {
  if (!range?.from) return 'Every expense you have entered';
  return `${formatDate(range.from)} – ${formatDate(range.to)}`;
}

function ChannelLabel({ channel }) {
  if (!channel) return <Chip size="small" variant="outlined" label="General" />;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <ChannelAvatar channel={channel} size={24} />
      <Typography variant="body2" noWrap title={channel.name}>
        {channel.name}
      </Typography>
    </Box>
  );
}

/** Amounts as bars, largest first, each with its share of the total. */
function Breakdown({ title, rows, total }) {
  const largest = Math.max(...rows.map((row) => row.amount), 1);
  return (
    <Section title={title} sx={{ mb: 0 }}>
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1.5 }}>
        {rows.map((row) => (
          <Box component="li" key={row.key}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ minWidth: 0 }}>{row.label}</Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
              >
                {formatMoney(row.amount)}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {Math.round((row.amount / total) * 100)}%
                </Typography>
              </Typography>
            </Box>
            <Box aria-hidden sx={{ mt: 0.75, height: 6, borderRadius: 3, bgcolor: 'action.hover', overflow: 'hidden' }}>
              <Box sx={{ height: '100%', width: `${(row.amount / largest) * 100}%`, bgcolor: 'primary.main', borderRadius: 3 }} />
            </Box>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

// Where the synced WaveMax expenses come from, and whether the last pull worked.
function WaveMaxNotice({ connection }) {
  const account = connection.accountEmail || connection.accountName || 'your WaveMax account';
  const synced = connection.lastSyncedAt
    ? `Last updated ${new Date(connection.lastSyncedAt).toLocaleString()}.`
    : 'The first update is in progress.';
  return (
    <Alert severity={connection.lastError ? 'warning' : 'info'} sx={{ mb: 2 }}>
      WaveMax costs from {account} are added each day as general expenses:{' '}
      {formatMoney(connection.usdPerMinute.aiAvatar)} a minute of AI Avatar video and{' '}
      {formatMoney(connection.usdPerMinute.realFootage)} a minute of Real Footage video. {synced}
      {connection.lastError && ` The last update failed: ${connection.lastError}`}
    </Alert>
  );
}

// Columns only shown on wider screens. On phones their values are a line
// under the category, so the amount and actions stay in view.
const WIDE_ONLY = { display: { xs: 'none', md: 'table-cell' } };

function ExpenseTable({ expenses, channelsById, onEdit, onDelete }) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={WIDE_ONLY}>Date</TableCell>
            <TableCell>Category</TableCell>
            <TableCell sx={WIDE_ONLY}>Description</TableCell>
            <TableCell sx={WIDE_ONLY}>Channel</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell sx={{ width: 96 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {expenses.map((expense) => {
            const channel = channelsById.get(expense.channelId);
            return (
              <TableRow key={expense.id} hover>
                <TableCell sx={{ ...WIDE_ONLY, whiteSpace: 'nowrap' }}>{formatDate(expense.date)}</TableCell>
                <TableCell sx={{ whiteSpace: { md: 'nowrap' } }}>
                  {expense.category}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: { xs: 'block', md: 'none' } }}
                  >
                    {[formatDate(expense.date), expense.description, channel ? channel.name : 'General']
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ ...WIDE_ONLY, minWidth: 160, color: expense.description ? INK : 'text.secondary' }}>
                  {expense.description || '—'}
                </TableCell>
                <TableCell sx={{ ...WIDE_ONLY, maxWidth: 240 }}>
                  <ChannelLabel channel={channel} />
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {formatMoney(expense.amount)}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {expense.source === 'wavemax' ? (
                    <Tooltip title="Pulled from your WaveMax account. It updates automatically.">
                      <Chip size="small" variant="outlined" label="Synced" />
                    </Tooltip>
                  ) : (
                    <>
                      <Tooltip title="Edit">
                        <IconButton size="small" aria-label="Edit expense" onClick={() => onEdit(expense)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" aria-label="Delete expense" onClick={() => onDelete(expense)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function ExpensesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const channelFilter = searchParams.get('channel') || '';
  const [selection, setSelection] = useState(loadSelection);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // { kind: 'add' | 'edit' | 'delete', expense }
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await ExpenseService.list(selectionQuery(selection), channelFilter));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load expenses.');
    } finally {
      setLoading(false);
    }
  }, [selection, channelFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const channelsById = useMemo(
    () => new Map((data?.channels || []).map((channel) => [channel.id, channel])),
    [data],
  );

  function changeSelection(next) {
    saveSelection(next);
    setSelection(next);
  }

  function changeChannelFilter(value) {
    setSearchParams(value ? { channel: value } : {}, { replace: true });
  }

  function saved() {
    setDialog(null);
    load();
  }

  const expenses = data?.expenses || [];
  const forChannels = (data?.byChannel || [])
    .filter((row) => row.channelId)
    .reduce((sum, row) => sum + row.amount, 0);
  const general = data?.byChannel.find((row) => !row.channelId)?.amount || 0;
  const channelCount = (data?.byChannel || []).filter((row) => row.channelId).length;

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, color: 'white', textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}
        >
          Expenses
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.85)' }}>
          What you spend on your channels.{' '}
          <Link component={RouterLink} to="/analytics" sx={{ color: 'white', fontWeight: 600 }}>
            Analytics
          </Link>{' '}
          takes it off your revenue to show your profit.
        </Typography>
      </Box>

      <Paper
        sx={{
          borderRadius: 2,
          background: surface.glass,
          boxShadow: 2,
          p: { xs: 2, md: 2.5 },
          mb: 2,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <RangePicker selection={selection} onChange={changeSelection} />
        <TextField
          select
          size="small"
          label="Channel"
          value={data ? channelFilter : ''}
          onChange={(event) => changeChannelFilter(event.target.value)}
          // "All channels" is the empty value, which a select hides by default.
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 200, maxWidth: '100%', bgcolor: 'background.paper' }}
        >
          <MenuItem value="">All channels</MenuItem>
          <MenuItem value={GENERAL}>General only</MenuItem>
          {(data?.channels || []).map((channel) => (
            <MenuItem key={channel.id} value={channel.id}>
              {channel.name}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {rangeLabel(data?.range)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Ranges run through today, here and for expenses in Analytics.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialog({ kind: 'add' })}
          disabled={!data}
        >
          Add expense
        </Button>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {data?.wavemax && <WaveMaxNotice connection={data.wavemax} />}

      {!data ? (
        loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: 'white' }} />
          </Box>
        )
      ) : (
        <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 150ms' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' },
              gap: 1.5,
              mb: 2,
            }}
          >
            <StatTile
              label="Total spent"
              value={formatMoney(data.total)}
              footnote={`${formatNumber(expenses.length)} ${expenses.length === 1 ? 'expense' : 'expenses'}`}
            />
            <StatTile
              label="For channels"
              value={formatMoney(forChannels)}
              footnote={`${formatNumber(channelCount)} ${channelCount === 1 ? 'channel' : 'channels'} · counted in their rows in Analytics`}
            />
            <StatTile
              label="General"
              value={formatMoney(general)}
              footnote="Not for one channel · counted in Analytics totals"
            />
          </Box>

          {expenses.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
                gap: 2,
                mb: 2,
              }}
            >
              <Breakdown
                title="By category"
                total={data.total}
                rows={data.byCategory.map((row) => ({
                  key: row.category,
                  label: (
                    <Typography variant="body2" noWrap>
                      {row.category}
                    </Typography>
                  ),
                  amount: row.amount,
                }))}
              />
              <Breakdown
                title="By channel"
                total={data.total}
                rows={data.byChannel.map((row) => ({
                  key: row.channelId || GENERAL,
                  label: <ChannelLabel channel={channelsById.get(row.channelId)} />,
                  amount: row.amount,
                }))}
              />
            </Box>
          )}

          <Section title="Expenses">
            {expenses.length ? (
              <ExpenseTable
                expenses={expenses}
                channelsById={channelsById}
                onEdit={(expense) => setDialog({ kind: 'edit', expense })}
                onDelete={(expense) => setDialog({ kind: 'delete', expense })}
              />
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  No expenses in this period.
                </Typography>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setDialog({ kind: 'add' })}>
                  Add expense
                </Button>
              </Box>
            )}
          </Section>
        </Box>
      )}

      {(dialog?.kind === 'add' || dialog?.kind === 'edit') && (
        <ExpenseDialog
          expense={dialog.expense}
          defaultChannelId={channelFilter === GENERAL ? null : channelFilter}
          channels={data.channels}
          categories={data.categories}
          onClose={() => setDialog(null)}
          onSaved={saved}
        />
      )}
      {dialog?.kind === 'delete' && (
        <DeleteExpenseDialog expense={dialog.expense} onClose={() => setDialog(null)} onDeleted={saved} />
      )}
    </Box>
  );
}
